/**
 * Design Tokens: Typography
 * Auto-generated from Figma Design System: Crop Circle System
 *
 * Typography tokens define text styles across the application.
 * Based on Figma text styles with Inter font family.
 * ✅ Verified from Figma API
 */

import { TextStyle } from 'react-native';

/**
 * Font Families
 */
export const fontFamilies = {
  primary: 'Inter',
  // Add additional font families if needed
} as const;

/**
 * Font Weights
 */
export const fontWeights = {
  regular: '400' as TextStyle['fontWeight'],
  medium: '500' as TextStyle['fontWeight'],
  semibold: '600' as TextStyle['fontWeight'],
  bold: '700' as TextStyle['fontWeight'],
} as const;

/**
 * Font Sizes
 */
export const fontSizes = {
  xs: 10,
  sm: 12,
  md: 14,
  lg: 16,
  xl: 18,
  '2xl': 20,
  '3xl': 24,
  '4xl': 28,
  '5xl': 32,
  '6xl': 36,
  '7xl': 48,
  '8xl': 57,
} as const;

/**
 * Line Heights
 */
export const lineHeights = {
  tight: 1.25,
  normal: 1.5,
  relaxed: 1.75,
} as const;

/**
 * Typography Styles
 * Organized by category matching Figma structure
 */

export const typography = {
  /**
   * Display - Large, impactful text
   * Used for splash screens, major headings, and prominent UI elements
   */
  display: {
    large: {
      fontFamily: fontFamilies.primary,
      fontSize: 48,
      fontWeight: fontWeights.regular,
      lineHeight: 56,
    },
    largeEmphasised: {
      fontFamily: fontFamilies.primary,
      fontSize: 48,
      fontWeight: fontWeights.bold,
      lineHeight: 56,
    },
    medium: {
      fontFamily: fontFamilies.primary,
      fontSize: 40,
      fontWeight: fontWeights.regular,
      lineHeight: 48,
    },
    mediumEmphasised: {
      fontFamily: fontFamilies.primary,
      fontSize: 40,
      fontWeight: fontWeights.bold,
      lineHeight: 48,
    },
    small: {
      fontFamily: fontFamilies.primary,
      fontSize: 32,
      fontWeight: fontWeights.regular,
      lineHeight: 40,
    },
    smallEmphasised: {
      fontFamily: fontFamilies.primary,
      fontSize: 32,
      fontWeight: fontWeights.bold,
      lineHeight: 40,
    },
  },

  /**
   * Headline - Major section headings
   * For important UI components and key content areas
   */
  headline: {
    large: {
      fontFamily: fontFamilies.primary,
      fontSize: 32,
      fontWeight: fontWeights.regular,
      lineHeight: 40,
    },
    largeEmphasised: {
      fontFamily: fontFamilies.primary,
      fontSize: 32,
      fontWeight: fontWeights.semibold, // 600 from Figma
      lineHeight: 40,
    },
    medium: {
      fontFamily: fontFamilies.primary,
      fontSize: 28,
      fontWeight: fontWeights.regular,
      lineHeight: 36,
    },
    mediumEmphasised: {
      fontFamily: fontFamilies.primary,
      fontSize: 28,
      fontWeight: fontWeights.bold,
      lineHeight: 36,
    },
    small: {
      fontFamily: fontFamilies.primary,
      fontSize: 24,
      fontWeight: fontWeights.regular,
      lineHeight: 32,
    },
    smallEmphasised: {
      fontFamily: fontFamilies.primary,
      fontSize: 24,
      fontWeight: fontWeights.bold,
      lineHeight: 32,
    },
  },

  /**
   * Title - Subheadings and card titles
   * For important labels and section headers
   */
  title: {
    large: {
      fontFamily: fontFamilies.primary,
      fontSize: 24,
      fontWeight: fontWeights.regular,
      lineHeight: 32,
    },
    largeEmphasised: {
      fontFamily: fontFamilies.primary,
      fontSize: 24,
      fontWeight: fontWeights.bold,
      lineHeight: 32,
    },
    medium: {
      fontFamily: fontFamilies.primary,
      fontSize: 20,
      fontWeight: fontWeights.regular,
      lineHeight: 28,
    },
    mediumEmphasised: {
      fontFamily: fontFamilies.primary,
      fontSize: 20,
      fontWeight: fontWeights.medium,
      lineHeight: 28,
    },
    small: {
      fontFamily: fontFamilies.primary,
      fontSize: 18,
      fontWeight: fontWeights.regular,
      lineHeight: 24,
    },
    smallEmphasised: {
      fontFamily: fontFamilies.primary,
      fontSize: 18,
      fontWeight: fontWeights.medium,
      lineHeight: 24,
    },
  },

  /**
   * Body - Regular text content
   * For paragraphs, descriptions, and standard UI text
   */
  body: {
    large: {
      fontFamily: fontFamilies.primary,
      fontSize: 18,
      fontWeight: fontWeights.regular,
      lineHeight: 28,
    },
    largeEmphasised: {
      fontFamily: fontFamilies.primary,
      fontSize: 18,
      fontWeight: fontWeights.medium,
      lineHeight: 28,
    },
    medium: {
      fontFamily: fontFamilies.primary,
      fontSize: 16,
      fontWeight: fontWeights.regular,
      lineHeight: 24,
    },
    mediumEmphasised: {
      fontFamily: fontFamilies.primary,
      fontSize: 16,
      fontWeight: fontWeights.medium,
      lineHeight: 24,
    },
    small: {
      fontFamily: fontFamilies.primary,
      fontSize: 14,
      fontWeight: fontWeights.regular,
      lineHeight: 20,
    },
    smallEmphasised: {
      fontFamily: fontFamilies.primary,
      fontSize: 14,
      fontWeight: fontWeights.medium,
      lineHeight: 20,
    },
  },

  /**
   * Label - UI labels and form labels
   * For small text in UI components
   */
  label: {
    large: {
      fontFamily: fontFamilies.primary,
      fontSize: 14,
      fontWeight: fontWeights.regular,
      lineHeight: 20,
    },
    largeEmphasised: {
      fontFamily: fontFamilies.primary,
      fontSize: 14,
      fontWeight: fontWeights.medium,
      lineHeight: 20,
    },
    medium: {
      fontFamily: fontFamilies.primary,
      fontSize: 12,
      fontWeight: fontWeights.regular,
      lineHeight: 16,
    },
    mediumEmphasised: {
      fontFamily: fontFamilies.primary,
      fontSize: 12,
      fontWeight: fontWeights.medium,
      lineHeight: 16,
    },
    small: {
      fontFamily: fontFamilies.primary,
      fontSize: 10,
      fontWeight: fontWeights.regular,
      lineHeight: 14,
    },
    smallEmphasised: {
      fontFamily: fontFamilies.primary,
      fontSize: 10,
      fontWeight: fontWeights.medium,
      lineHeight: 14,
    },
  },

  /**
   * Link - Hyperlinks and interactive text
   * For call-to-action links
   */
  link: {
    large: {
      fontFamily: fontFamilies.primary,
      fontSize: 16,
      fontWeight: fontWeights.regular,
      lineHeight: 24,
      textDecorationLine: 'underline' as TextStyle['textDecorationLine'],
    },
    largeEmphasised: {
      fontFamily: fontFamilies.primary,
      fontSize: 16,
      fontWeight: fontWeights.medium,
      lineHeight: 24,
      textDecorationLine: 'underline' as TextStyle['textDecorationLine'],
    },
    medium: {
      fontFamily: fontFamilies.primary,
      fontSize: 14,
      fontWeight: fontWeights.regular,
      lineHeight: 20,
      textDecorationLine: 'underline' as TextStyle['textDecorationLine'],
    },
    mediumEmphasised: {
      fontFamily: fontFamilies.primary,
      fontSize: 14,
      fontWeight: fontWeights.medium,
      lineHeight: 20,
      textDecorationLine: 'underline' as TextStyle['textDecorationLine'],
    },
    small: {
      fontFamily: fontFamilies.primary,
      fontSize: 12,
      fontWeight: fontWeights.regular,
      lineHeight: 16,
      textDecorationLine: 'underline' as TextStyle['textDecorationLine'],
    },
    smallEmphasised: {
      fontFamily: fontFamilies.primary,
      fontSize: 12,
      fontWeight: fontWeights.medium,
      lineHeight: 16,
      textDecorationLine: 'underline' as TextStyle['textDecorationLine'],
    },
  },
} as const;

/**
 * Typography types for TypeScript
 */
export type TypographyToken = typeof typography;
export type TypographyCategory = keyof typeof typography;
